import type { CSSProperties, FC, ReactNode } from 'react';
import { useRef, useState } from 'react';
import classNames from 'classnames';
import {
  useFloating,
  arrow,
  autoUpdate,
  useDismiss,
  useRole,
  useInteractions,
  offset,
  useClick,
  FloatingFocusManager,
  shift,
  size,
  useTransitionStatus,
  FloatingPortal
} from '@floating-ui/react';

import ArrowIcon from 'app/icons/arrow-min.svg';
import { getOffset, getPlacement } from 'app/utils/floating-ui';

import style from './style.module.scss';

interface Option {
  label: ReactNode;
  value: string;
}

interface Props {
  placeholder?: string;
  options: Option[];
  value: string | undefined;
  onChange: (value: string) => void;
  className?: string;
  style?: CSSProperties;
  position?: Position;
  openedStyle?: CSSProperties;
  withArrow?: boolean;
  portalClassName?: string;
  arrowIconClassName?: string;
  optionClassName?: string;
  label?: ReactNode;
  contentClassName?: string;
  alignment?: Alignment;
  offset?: { x: number; y: number };
}

const Dropdown: FC<Props> = ({
  placeholder,
  options,
  value,
  onChange,
  className,
  style: css,
  position: dropdownPosition = 'bottom',
  openedStyle,
  withArrow = true,
  portalClassName,
  arrowIconClassName,
  optionClassName,
  contentClassName,
  label,
  alignment = 'middle',
  offset: definedOffset
}) => {
  const arrowRef = useRef<HTMLDivElement | null>(null);
  const [opened, setOpened] = useState(false);

  const selected = options.find(o => o.value === value);
  const dropdownLabel = label ?? selected?.label ?? placeholder;

  const selectOption = (value: string) => {
    onChange(value);
    setOpened(false);
  };

  const { x, y, refs, strategy, context, placement, middlewareData } = useFloating({
    middleware: [
      offset(getOffset(definedOffset, dropdownPosition)),
      size({
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`
          });
        }
      }),
      shift(),
      arrow({ element: arrowRef })
    ],
    open: opened,
    onOpenChange: setOpened,
    whileElementsMounted: autoUpdate,
    placement: getPlacement(dropdownPosition, alignment)
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const { isMounted, status } = useTransitionStatus(context, { duration: 300 });

  const { x: arrowX, y: arrowY } = middlewareData?.arrow || { x: undefined, y: undefined };

  const buttonClassName = classNames(
    style.button,
    {
      [style.button_with_placeholder]: !selected
    },
    className
  );

  const arrowIconClassNames = {
    top: style.arrowIcon_top,
    bottom: style.arrowIcon_bottom,
    left: style.arrowIcon_left,
    right: style.arrowIcon_right
  };

  const portalPositionnedClassName = classNames(
    withArrow
      ? {
          [style.top]: placement.includes('top'),
          [style.left]: placement.includes('left'),
          [style.right]: placement.includes('right'),
          [style.bottom]: placement.includes('bottom')
        }
      : ''
  );

  return (
    <>
      <button
        ref={refs.setReference}
        className={buttonClassName}
        style={{ ...css, ...(openedStyle && opened ? openedStyle : {}) }}
        type="button"
        {...getReferenceProps()}
      >
        {dropdownLabel}
        <ArrowIcon
          className={classNames(arrowIconClassNames[dropdownPosition], arrowIconClassName)}
        />
      </button>
      {isMounted && (
        <FloatingFocusManager context={context} guards={false} initialFocus={-1}>
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
                width: 'max-content'
              }}
              {...getFloatingProps()}
              className={classNames(
                style.portal,
                {
                  [style.portalWithArrow]: withArrow
                },
                portalPositionnedClassName,
                portalClassName
              )}
              data-status={status}
            >
              <div>
                {withArrow && (
                  <div
                    style={{
                      left: arrowX !== undefined ? `${arrowX}px` : '',
                      top: arrowY !== undefined ? `${arrowY}px` : ''
                    }}
                    ref={arrowRef}
                    className={style.triangle}
                  />
                )}
                <div className={classNames(style.content, contentClassName)}>
                  {options.map(option => (
                    <button
                      key={option.value}
                      className={classNames(style.option, optionClassName)}
                      onClick={() => selectOption(option.value)}
                      type="button"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </FloatingPortal>
        </FloatingFocusManager>
      )}
    </>
  );
};

Dropdown.displayName = 'Dropdown';

export default Dropdown;
